import { supabase } from '@/integrations/supabase/client';

export interface RSVPQuestion {
  id: string;
  question: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'number' | 'email' | 'phone';
  options?: string[];
  required: boolean;
  category: string;
}

export interface ProfessionalEventTemplate {
  id: string;
  user_id: string;
  client_id?: string | null;
  name: string;
  description?: string | null;
  category: string;
  event_type: string;
  default_options: any;
  default_duration_hours?: number | null;
  default_reminder_days?: number | null;
  industry_rsvp_questions?: RSVPQuestion[];
  template_design_config?: any;
  is_public: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export const professionalTemplateService = {
  async getUserTemplates(): Promise<ProfessionalEventTemplate[]> {
    console.log('Fetching user professional templates');
    
    const { data, error } = await supabase
      .from('professional_event_templates')
      .select(`
        *,
        clients(name)
      `)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }

    return (data || []).map(template => ({
      ...template,
      event_type: template.event_type as 'voting' | 'rsvp',
      industry_rsvp_questions: Array.isArray(template.industry_rsvp_questions) 
        ? (template.industry_rsvp_questions as unknown) as RSVPQuestion[]
        : []
    }));
  },

  async getPublicTemplates(): Promise<ProfessionalEventTemplate[]> {
    console.log('Fetching public professional templates');
    
    const { data, error } = await supabase
      .from('professional_event_templates')
      .select('*')
      .eq('is_public', true)
      .order('usage_count', { ascending: false });

    if (error) {
      console.error('Error fetching public templates:', error);
      throw error;
    }

    return (data || []).map(template => ({
      ...template,
      event_type: template.event_type as 'voting' | 'rsvp',
      industry_rsvp_questions: Array.isArray(template.industry_rsvp_questions) 
        ? (template.industry_rsvp_questions as unknown) as RSVPQuestion[]
        : []
    }));
  },

  async createTemplate(template: {
    name: string;
    description?: string;
    category: string;
    event_type: string;
    client_id?: string;
    default_options: any;
    default_duration_hours?: number;
    default_reminder_days?: number;
    industry_rsvp_questions?: RSVPQuestion[];
    template_design_config?: any;
    is_public: boolean;
  }): Promise<ProfessionalEventTemplate> {
    console.log('Creating professional template:', template);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('professional_event_templates')
      .insert({
        user_id: user.id,
        name: template.name,
        description: template.description,
        category: template.category,
        event_type: template.event_type,
        client_id: template.client_id,
        default_options: template.default_options,
        default_duration_hours: template.default_duration_hours,
        default_reminder_days: template.default_reminder_days,
        industry_rsvp_questions: (template.industry_rsvp_questions || []) as any,
        template_design_config: template.template_design_config || {},
        is_public: template.is_public,
        usage_count: 0
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating template:', error);
      throw error;
    }

    return {
      ...data,
      event_type: data.event_type as 'voting' | 'rsvp',
      industry_rsvp_questions: Array.isArray(data.industry_rsvp_questions) 
        ? (data.industry_rsvp_questions as unknown) as RSVPQuestion[]
        : []
    };
  },

  async updateTemplate(id: string, updates: any): Promise<ProfessionalEventTemplate> {
    console.log('Updating professional template:', id, updates);
    
    const { data, error } = await supabase
      .from('professional_event_templates')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating template:', error);
      throw error;
    }

    return {
      ...data,
      event_type: data.event_type as 'voting' | 'rsvp',
      industry_rsvp_questions: Array.isArray(data.industry_rsvp_questions) 
        ? (data.industry_rsvp_questions as unknown) as RSVPQuestion[]
        : []
    };
  },

  async deleteTemplate(id: string): Promise<void> {
    console.log('Deleting professional template:', id);
    
    const { error } = await supabase
      .from('professional_event_templates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  async incrementUsage(id: string): Promise<void> {
    console.log('Incrementing template usage:', id);
    
    const { error } = await supabase.rpc('increment_template_usage', { template_id: id });
    
    if (error) {
      console.error('Error incrementing usage:', error);
      // Don't throw error as this is not critical
    }
  }
};